import { useContainerWidth } from "./flowsChartShared";
import type { CapeSegmentDto } from "../api";

/**
 * The academic/Vanguard-style CAPE fair-value-range chart (2026-09-27,
 * x-axis revised 2026-09-27 on direct user feedback):
 * a drifting grey envelope (expanding mean +/- 1 SD of CAPE itself) with
 * the actual CAPE line weaving through it. A SEPARATE chart from
 * ValuationBandChart's static mean/SD band -- different metric (smoothed-
 * earnings CAPE vs raw trailing P/E), different band shape (drifting vs
 * flat), shown alongside it, never replacing it.
 *
 * TWO SEGMENTS, never one spliced line -- see backend
 * app/factory/index_cape.py's module docstring for why: a 10-year
 * EPS-smoothing window computed across the 2021-04-01 earnings-basis
 * switch would blend two incompatible EPS definitions.
 *
 * X-AXIS is POINT-INDEX (like ValuationBandChart/PriceChart), NOT
 * calendar-time. An earlier version used a real calendar-time axis so the
 * multi-year gap between segments rendered "proportionally" -- in practice
 * that gave the ~21yr pre-2021 span and the ~2yr no-data gap so much of the
 * width that the real, several-year post-2021 segment was squeezed into an
 * illegibly small sliver at the right edge. Point-index gives every plotted
 * point equal width regardless of the calendar gap between segments, so
 * the post-2021 segment is exactly as visually legible as the pre-2021 one.
 * The gap itself is NOT hidden -- an explicit dashed break marker plus a
 * "no valid smoothing window here" label sits between the two segments'
 * indices, so it stays an honest, visible fact rather than a smooth
 * transition implying continuous data.
 */

const HEIGHT = 260;
const MARGIN = { top: 14, right: 12, bottom: 22, left: 44 };
const TICK_COUNT = 10;

function formatTick(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

function daysBetween(a: string, b: string): number {
  return (new Date(b).getTime() - new Date(a).getTime()) / 86400000;
}

export function CapeChart({
  pre, post,
}: {
  pre: CapeSegmentDto | null;
  post: CapeSegmentDto | null;
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();

  const segments = [pre, post].filter((s): s is CapeSegmentDto => !!s && s.available && s.points.length > 0);
  if (segments.length === 0) {
    const reasons = [pre, post].filter((s): s is CapeSegmentDto => !!s).map((s) => s.reason).filter(Boolean);
    return (
      <div ref={ref} className="empty-state" style={{ minHeight: 100 }}>
        Not enough history for a CAPE chart yet{reasons.length ? ` -- ${reasons.join("; ")}` : ""}.
      </div>
    );
  }
  if (width === 0) return <div ref={ref} style={{ minHeight: HEIGHT }} />;

  const plotW = Math.max(width - MARGIN.left - MARGIN.right, 10);
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom;

  // Flatten both segments into one index-ordered point list, remembering
  // where each segment starts/ends so bands/lines can still be drawn
  // per-segment (never blended across the gap) while sharing one x-scale.
  type FlatPoint = { date: string; cape: number; band_mean: number | null; band_sd: number | null; segIdx: number };
  const flat: FlatPoint[] = segments.flatMap((s, si) => s.points.map((p) => ({ ...p, segIdx: si })));
  const n = flat.length;
  const lastIdx = Math.max(n - 1, 1);

  const allValues = flat.flatMap((p) => [
    p.cape, p.band_mean !== null && p.band_sd !== null ? p.band_mean + p.band_sd : p.cape,
    p.band_mean !== null && p.band_sd !== null ? p.band_mean - p.band_sd : p.cape,
  ]);
  const yMin = Math.min(...allValues);
  const yMax = Math.max(...allValues);
  const yPad = (yMax - yMin) * 0.06 || 1;
  const domainMin = Math.max(yMin - yPad, 0);
  const domainMax = yMax + yPad;

  const xAtIdx = (i: number) => MARGIN.left + (i / lastIdx) * plotW;
  const yAt = (v: number) => MARGIN.top + (1 - (v - domainMin) / (domainMax - domainMin)) * plotH;

  const tickCount = Math.min(TICK_COUNT, n);
  const xTicks = Array.from({ length: tickCount }, (_, i) => {
    const idx = Math.round((i / (tickCount - 1 || 1)) * lastIdx);
    return { idx, date: flat[idx].date };
  });

  // The gap between segments (if two segments are both present): the last
  // pre-2021 index and the first post-2021 index sit ADJACENT on this
  // index-based x-axis, so a visible dashed break plus the real elapsed
  // gap (read from the actual dates, not assumed) marks the boundary.
  const gapAfterIdx = segments.length === 2 ? segments[0].points.length - 1 : -1;
  const gapYears = gapAfterIdx >= 0
    ? Math.round((daysBetween(flat[gapAfterIdx].date, flat[gapAfterIdx + 1].date) / 365) * 10) / 10
    : 0;

  return (
    <div ref={ref}>
      <div className="cape-legend">
        <span><i className="cape-swatch line" /> CAPE (nominal, smoothed-earnings)</span>
        <span><i className="cape-swatch band" /> fair-value range (expanding mean ± 1σ)</span>
      </div>
      <svg width={width} height={HEIGHT} role="img" aria-label="CAPE fair-value range chart">
        {segments.map((seg, si) => {
          const offset = si === 0 ? 0 : segments[0].points.length;
          const withBand = seg.points
            .map((p, i) => ({ p, idx: offset + i }))
            .filter(({ p }) => p.band_mean !== null && p.band_sd !== null);
          const upperPath = withBand.map(({ p, idx }, i) => `${i === 0 ? "M" : "L"}${xAtIdx(idx).toFixed(1)},${yAt(p.band_mean! + p.band_sd!).toFixed(1)}`).join(" ");
          const lowerPath = [...withBand].reverse().map(({ p, idx }) => `L${xAtIdx(idx).toFixed(1)},${yAt(p.band_mean! - p.band_sd!).toFixed(1)}`).join(" ");
          const bandPath = withBand.length > 1 ? `${upperPath} ${lowerPath} Z` : "";
          const linePath = seg.points.map((p, i) => `${i === 0 ? "M" : "L"}${xAtIdx(offset + i).toFixed(1)},${yAt(p.cape).toFixed(1)}`).join(" ");
          return (
            <g key={si}>
              {bandPath && <path d={bandPath} fill="#8a91a033" stroke="none" />}
              <path d={linePath} fill="none" stroke="#1a9d8a" strokeWidth={1.6} />
            </g>
          );
        })}

        {gapAfterIdx >= 0 && (
          <>
            <line x1={xAtIdx(gapAfterIdx + 0.5)} x2={xAtIdx(gapAfterIdx + 0.5)} y1={MARGIN.top} y2={HEIGHT - MARGIN.bottom}
                  stroke="#b8860b" strokeWidth={1} strokeDasharray="3 3" opacity={0.7} />
            <text x={xAtIdx(gapAfterIdx + 0.5) + 4} y={MARGIN.top + 10} fontSize={8.5} fill="#b8860b">
              earnings-basis switch
            </text>
            <text x={xAtIdx(gapAfterIdx + 0.5) + 4} y={MARGIN.top + 21} fontSize={8} fill="#b8860b" opacity={0.85}>
              ~{gapYears}yr gap -- no valid smoothing window
            </text>
          </>
        )}

        {xTicks.map(({ idx, date }, i) => (
          <text key={i} x={xAtIdx(idx)} y={HEIGHT - 6} fontSize={9} fill="#8a91a0" textAnchor="middle">
            {formatTick(date)}
          </text>
        ))}
        <text x={4} y={yAt(domainMax) + 8} fontSize={9} fill="#8a91a0">{domainMax.toFixed(0)}</text>
        <text x={4} y={yAt(domainMin) + 2} fontSize={9} fill="#8a91a0">{domainMin.toFixed(0)}</text>
      </svg>
    </div>
  );
}
